import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalState } from "context/GlobalContext";
import axios from "axios";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearRounded from "@mui/icons-material/ClearRounded";
import SearchRoundedIcon from "@mui/icons-material/Search";
import RefreshRoundedIcon from "@mui/icons-material/Refresh";
import { userTypes } from "data/authData";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import AddNewUser from "./AddNewUser/AddNewUser";
import UserInfoModal from "./UserInfoModal/UserInfoModal";
import { RoleChips } from "./shared/RoleAccessSection";
import { getRoleKeys, getRoleLabel } from "./shared/roleHelpers";
import UsersLoadingSkeleton from "./shared/UsersLoadingSkeleton";
import {
	colorFromName,
	getInitials,
	modalInputSx,
	modalSaveBtnSx,
	UserModalHeader,
	UserPageHeader,
	UserStatCard,
	UserTypeChip,
	userModalPaperProps,
	userUi,
} from "./shared/userUi";

const dash = (value) => (value == null || String(value).trim() === "" ? "—" : value);

const ROW_HEIGHT = 56;

const TableCellBox = ({ children }) => (
	<Box
		sx={{
			width: "100%",
			display: "flex",
			alignItems: "center",
			px: 1,
			boxSizing: "border-box",
			overflow: "hidden",
			minWidth: 0,
			minHeight: 40,
		}}>
		{children}
	</Box>
);

const CellText = ({ children }) => (
	<Typography noWrap sx={{ fontSize: 14, color: userUi.textPrimary, lineHeight: 1.3, width: "100%" }}>
		{children}
	</Typography>
);

const matchesSearch = (row, query) => {
	if (!query) return true;
	const roleText = getRoleKeys(row.role)
		.map(getRoleLabel)
		.join(" ");
	const haystack = [
		row.name,
		row.email,
		row.employee_id,
		row.department,
		row.designation,
		row.mobile_no,
		row.type,
		roleText,
	]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();
	return haystack.includes(query);
};

const userTableColumns = [
	{
		key: "name",
		name: "User",
		minWidth: 240,
		flex: 1.4,
		headerAlign: "left",
		align: "left",
		valueGetter: ({ row }) => row.name || "",
		renderCell: ({ row }) => (
			<TableCellBox>
				<Avatar
					sx={{
						width: 32,
						height: 32,
						fontSize: 12,
						fontWeight: 700,
						bgcolor: colorFromName(row.name),
						flexShrink: 0,
						mr: 1.25,
					}}>
					{getInitials(row.name)}
				</Avatar>
				<Box minWidth={0} overflow="hidden">
					<Typography noWrap sx={{ fontWeight: 600, fontSize: 14, color: userUi.textPrimary, lineHeight: 1.3 }}>
						{row.name || "—"}
					</Typography>
					<Typography noWrap sx={{ fontSize: 12, color: userUi.textSecondary, lineHeight: 1.3 }}>
						{row.email || "—"}
					</Typography>
				</Box>
			</TableCellBox>
		),
	},
	{
		key: "type",
		name: "Type",
		minWidth: 110,
		maxWidth: 110,
		flex: 0,
		headerAlign: "left",
		align: "left",
		renderCell: ({ row }) => (
			<TableCellBox>
				<UserTypeChip label={row.type} />
			</TableCellBox>
		),
	},
	{
		key: "employee_id",
		name: "Employee ID",
		minWidth: 130,
		maxWidth: 130,
		flex: 0,
		headerAlign: "left",
		align: "left",
		valueGetter: ({ row }) => dash(row.employee_id),
		renderCell: ({ row }) => (
			<TableCellBox>
				<CellText>{dash(row.employee_id)}</CellText>
			</TableCellBox>
		),
	},
	{
		key: "department",
		name: "Department",
		minWidth: 150,
		flex: 0.8,
		headerAlign: "left",
		align: "left",
		valueGetter: ({ row }) => dash(row.department),
		renderCell: ({ row }) => (
			<TableCellBox>
				<CellText>{dash(row.department)}</CellText>
			</TableCellBox>
		),
	},
	{
		key: "designation",
		name: "Designation",
		minWidth: 150,
		flex: 0.8,
		headerAlign: "left",
		align: "left",
		valueGetter: ({ row }) => dash(row.designation),
		renderCell: ({ row }) => (
			<TableCellBox>
				<CellText>{dash(row.designation)}</CellText>
			</TableCellBox>
		),
	},
	{
		key: "mobile_no",
		name: "Mobile",
		minWidth: 130,
		maxWidth: 130,
		flex: 0,
		headerAlign: "left",
		align: "left",
		valueGetter: ({ row }) => dash(row.mobile_no),
		renderCell: ({ row }) => (
			<TableCellBox>
				<CellText>{dash(row.mobile_no)}</CellText>
			</TableCellBox>
		),
	},
	{
		key: "role",
		name: "Roles",
		minWidth: 220,
		flex: 1.2,
		headerAlign: "left",
		align: "left",
		valueGetter: ({ row }) => getRoleKeys(row.role).map(getRoleLabel).join(", "),
		renderCell: ({ row }) => (
			<TableCellBox>
				<RoleChips role={row.role} max={2} emptyLabel="—" nowrap />
			</TableCellBox>
		),
	},
];

const filterRows = (rows, filters) => {
	let next = rows || [];
	Object.entries(filters || {}).forEach(([key, value]) => {
		if (value == null || String(value).trim() === "") return;
		const q = String(value).toLowerCase();
		const col = userTableColumns.find((c) => c.key === key);
		next = next.filter((row) => {
			const raw = row?.[key];
			const display =
				typeof col?.valueGetter === "function"
					? String(col.valueGetter({ row, value: raw, column: col, field: key }) ?? "")
					: String(raw ?? "");
			return display.toLowerCase().includes(q);
		});
	});
	return next;
};

const Users = () => {
	const [allUsers, setAllUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState();
	const [readOnly, setReadOnly] = useState(true);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [newUserModalOpen, setNewUserModalOpen] = useState(false);
	const [typeFilter, setTypeFilter] = useState("ALL");
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(true);
	const [filterLoading, setFilterLoading] = useState(false);

	const [pageData, setPageData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterObj, setFilterObj] = useState({});
	const [gridKey, setGridKey] = useState(0);

	const sourceRef = useRef([]);
	const extraFiltersRef = useRef({ search: "", type: "ALL" });
	const skipFilterBumpRef = useRef(true);
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();

	const bumpFilters = () => setFilterObj((prev) => ({ ...prev }));

	const fetchUsers = async () => {
		setLoading(true);
		try {
			const res = await axios.get("/api/auth/users");
			const rows = Array.isArray(res.data) ? res.data : [];
			skipFilterBumpRef.current = true;
			sourceRef.current = rows;
			setAllUsers(rows);
			setGridKey((key) => key + 1);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(error) || "Unable to load users",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		sourceRef.current = allUsers;
		if (skipFilterBumpRef.current) {
			skipFilterBumpRef.current = false;
			return;
		}
		bumpFilters();
	}, [allUsers]);

	const stats = useMemo(
		() => ({
			all: allUsers.length,
			internal: allUsers.filter((user) => user.type === userTypes.internal).length,
			external: allUsers.filter((user) => user.type === userTypes.external).length,
		}),
		[allUsers]
	);

	const handleTypeFilter = (next) => {
		if (next === typeFilter || filterLoading) return;
		setTypeFilter(next);
		extraFiltersRef.current.type = next;
		setFilterLoading(true);
	};

	useEffect(() => {
		if (!filterLoading) return undefined;
		const timer = setTimeout(() => {
			setGridKey((key) => key + 1);
			setFilterLoading(false);
		}, 280);
		return () => clearTimeout(timer);
	}, [filterLoading]);

	const handleSearchChange = (value) => {
		setSearch(value);
		extraFiltersRef.current.search = value;
		bumpFilters();
	};

	const getServerSideData = useCallback(async (limit, offset, filters) => {
		const { search: query, type } = extraFiltersRef.current;
		let next = sourceRef.current || [];
		if (type !== "ALL") next = next.filter((user) => user.type === type);
		if (query.trim()) next = next.filter((user) => matchesSearch(user, query.trim().toLowerCase()));
		next = filterRows(next, filters);
		return {
			rows: next.slice(offset, offset + limit),
			total_rows_count: next.length,
		};
	}, []);

	const handleGridRowClick = ({ row }) => {
		setReadOnly(true);
		setSelectedUser(row);
		setEditModalOpen(true);
	};

	const handleEditModalClose = () => {
		setReadOnly(true);
		setEditModalOpen(false);
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 64px)",
				bgcolor: userUi.pageBg,
				px: { xs: 1.5, md: 3 },
				py: { xs: 2, md: 3 },
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
			}}>
			<Box
				sx={{
					width: "100%",
					maxWidth: 1400,
					mx: "auto",
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
				}}>
				<UserPageHeader
					title="Users"
					subtitle="Create logins and manage department, service, and custom access"
					sx={{ mb: 2, flexShrink: 0 }}
					actions={
						<>
							<IconButton onClick={fetchUsers} size="small" sx={{ border: `1px solid ${userUi.border}`, borderRadius: 1.5 }}>
								<RefreshRoundedIcon fontSize="small" />
							</IconButton>
							<Button
								variant="contained"
								startIcon={<AddRoundedIcon />}
								onClick={() => setNewUserModalOpen(true)}
								sx={modalSaveBtnSx}>
								New user
							</Button>
						</>
					}
				/>

				<Box display="flex" gap={1.5} flexWrap="wrap" sx={{ mb: 2, flexShrink: 0 }}>
					<UserStatCard
						label="All users"
						value={stats.all}
						selected={typeFilter === "ALL"}
						onClick={() => handleTypeFilter("ALL")}
						loading={loading}
					/>
					<UserStatCard
						label="Internal"
						value={stats.internal}
						selected={typeFilter === userTypes.internal}
						onClick={() => handleTypeFilter(userTypes.internal)}
						loading={loading}
					/>
					<UserStatCard
						label="External"
						value={stats.external}
						selected={typeFilter === userTypes.external}
						onClick={() => handleTypeFilter(userTypes.external)}
						loading={loading}
					/>
				</Box>

				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						width: "100%",
						border: `1px solid ${userUi.border}`,
						borderRadius: userUi.radius,
						overflow: "hidden",
						bgcolor: "#fff",
						display: "flex",
						flexDirection: "column",
						"& > .MuiPaper-root": {
							flex: 1,
							minHeight: 0,
							height: "100% !important",
							boxShadow: "none",
						},
					}}>
					{(loading && !allUsers.length) || filterLoading ? (
						<UsersLoadingSkeleton rows={10} rowHeight={ROW_HEIGHT} />
					) : (
					<TanstackDatagrid
						key={gridKey}
						columns={userTableColumns}
						pageData={pageData}
						setPageData={setPageData}
						rowCount={rowCount}
						setRowCount={setRowCount}
						filterObj={filterObj}
						setFilterObj={setFilterObj}
						getServerSideData={getServerSideData}
						rowsPerPage={25}
						emptyMessage="No users found"
						handleRowClick={handleGridRowClick}
						getRowId={(row) => row.id}
						headerRowStyle={{ background: "#FAFAFA" }}
						rowHeight={ROW_HEIGHT}
						headerHeight={48}
						isLoading={loading}>
						<TextField
							size="small"
							value={search}
							onChange={(e) => handleSearchChange(e.target.value)}
							placeholder="Search name, email, ID, role…"
							sx={{ ...modalInputSx, width: { xs: "100%", sm: 320 } }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchRoundedIcon sx={{ color: userUi.textMuted, fontSize: 20 }} />
									</InputAdornment>
								),
							}}
						/>
					</TanstackDatagrid>
					)}
				</Box>
			</Box>

			<Dialog
				open={newUserModalOpen}
				onClose={() => setNewUserModalOpen(false)}
				scroll="body"
				maxWidth="md"
				fullWidth
				PaperProps={userModalPaperProps}>
				<Box sx={{ position: "relative" }}>
					<UserModalHeader title="Add new user" subtitle="Create a login and assign access" />
					<IconButton
						onClick={() => setNewUserModalOpen(false)}
						size="small"
						sx={{ position: "absolute", top: 16, right: 16 }}>
						<ClearRounded fontSize="small" />
					</IconButton>
				</Box>
				<Box sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
					<AddNewUser
						key={String(newUserModalOpen)}
						allUsers={allUsers}
						setAllUsers={setAllUsers}
						setNewUserModalOpen={setNewUserModalOpen}
					/>
				</Box>
			</Dialog>

			<Dialog
				open={editModalOpen}
				onClose={handleEditModalClose}
				scroll="body"
				maxWidth="md"
				fullWidth
				PaperProps={userModalPaperProps}>
				<Box sx={{ position: "relative" }}>
					<UserModalHeader
						title={selectedUser?.name || "User details"}
						subtitle={selectedUser?.email || "View and update profile and access"}
					/>
					<IconButton onClick={handleEditModalClose} size="small" sx={{ position: "absolute", top: 16, right: 16 }}>
						<ClearRounded fontSize="small" />
					</IconButton>
				</Box>
				<Box sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
					{selectedUser ? (
						<UserInfoModal
							key={selectedUser.id}
							allUsers={allUsers}
							setAllUsers={setAllUsers}
							selectedUser={selectedUser}
							setSelectedUser={setSelectedUser}
							setEditModalOpen={setEditModalOpen}
							readOnly={readOnly}
							setReadOnly={setReadOnly}
						/>
					) : null}
				</Box>
			</Dialog>
		</Box>
	);
};

export default Users;
